import type { TTicketData, TTicketMessage } from '@cascade/api-client/shared';
import { isKeyOf } from '@cascade/core/utils';
import type { TChatMessageProps } from '@cascade/ui-library';
import { buildChatFiles } from '#helpers/index.ts';
import type {
  TThreadParticipants,
  TTicketChangeKey,
  TTranslateChange,
} from './Ticket.types.ts';

const TEXT_MESSAGE_TYPE = 'text';
const STATUS_CHANGE_TYPE = 'status_change';
const ASSIGNMENT_CHANGE_TYPE = 'assignment_change';
const PRIORITY_CHANGE_TYPE = 'priority_change';

export const MINIMUM_TICKET_MESSAGE_LENGTH = 3;

const statusChangeKeys: Record<TTicketData['status'], TTicketChangeKey> = {
  open: 'ticket.change.opened',
  pending: 'ticket.change.pending',
  in_progress: 'ticket.change.inProgress',
  reopened: 'ticket.change.reopened',
  closed: 'ticket.change.resolved',
};

const getChangeKey = (message: TTicketMessage): TTicketChangeKey => {
  const { type, after } = message.data;

  if (type === STATUS_CHANGE_TYPE && isKeyOf(statusChangeKeys, after)) {
    return statusChangeKeys[after];
  }

  if (type === ASSIGNMENT_CHANGE_TYPE) {
    return 'ticket.change.assigned';
  }

  if (type === PRIORITY_CHANGE_TYPE) {
    return 'ticket.change.priority';
  }

  return 'ticket.change.updated';
};

export const getFirstTextMessageIndex = (messages: TTicketMessage[]): number =>
  messages.findIndex((message) => message.data.type === TEXT_MESSAGE_TYPE);

export const buildThreadMessage = (
  message: TTicketMessage,
  participants: TThreadParticipants,
  translateChange: TTranslateChange,
): TChatMessageProps => {
  const { userId, userName, selfName, supportName } = participants;
  const isOwn = message.sender_id === userId;
  const otherName = message.sender_handle || supportName;

  if (message.data.type !== TEXT_MESSAGE_TYPE) {
    const actor = isOwn ? selfName : otherName;

    return {
      messageId: message.id,
      sender: 'system',
      message: translateChange(getChangeKey(message), { actor }),
    };
  }

  return {
    messageId: message.id,
    name: isOwn ? userName : otherName,
    sender: isOwn ? 'active-user' : 'human',
    message: message.data.message ?? '',
    files: buildChatFiles(message.data.files),
  };
};
